import React, { useState } from "react";
import { toast } from "sonner";
import { Briefcase, Loader2, AlertTriangle } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { OPPORTUNITY_TYPES, localize } from "../lib/meta";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

export function OpportunityInquiryDialog({ open, onOpenChange, psychologist }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ opportunity_type: "group_sessions", description: "", location: "", modality: "online", timeframe: "", language: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/psychologists/${psychologist.id}/opportunity-inquiry`, form);
      toast.success(t("inquiry.sent"));
      onOpenChange(false);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("inquiry.send_error")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="opportunity-inquiry-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Briefcase className="h-5 w-5 text-terracotta" />{t("inquiry.title")}</DialogTitle>
          <DialogDescription>{t("inquiry.desc").replace("{name}", psychologist.full_name)}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>{t("inquiry.type_label")}</Label>
            <Select value={form.opportunity_type} onValueChange={(v) => set("opportunity_type", v)}>
              <SelectTrigger data-testid="inq-type"><SelectValue /></SelectTrigger>
              <SelectContent>{OPPORTUNITY_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{localize(OPPORTUNITY_TYPES, o.value, lang)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("initiatives.description")}</Label>
            <Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} required data-testid="inq-description" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>{t("inquiry.location")}</Label><Input value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="inq-location" /></div>
            <div>
              <Label>{t("inquiry.modality")}</Label>
              <Select value={form.modality} onValueChange={(v) => set("modality", v)}>
                <SelectTrigger data-testid="inq-modality"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="online">{t("common.online")}</SelectItem>
                  <SelectItem value="in_person">{t("common.in_person")}</SelectItem>
                  <SelectItem value="hybrid">{t("inquiry.hybrid")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t("inquiry.timeframe")}</Label><Input value={form.timeframe} onChange={(e) => set("timeframe", e.target.value)} data-testid="inq-timeframe" /></div>
            <div><Label>{t("inquiry.language_required")}</Label><Input value={form.language} onChange={(e) => set("language", e.target.value)} data-testid="inq-language" /></div>
          </div>
          <p className="flex items-start gap-2 rounded-lg bg-cream px-3 py-2.5 text-xs text-muted-foreground" data-testid="inq-warning">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {t("inquiry.warning")}
          </p>
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="inq-submit">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t("inquiry.submit")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
